/**
 * The member profile drawer.
 *
 * Six tabs: Overview, Memberships, Payments, Attendance, Timeline and
 * Details. Everything shown is derived through NetlyPayManager, so the balance
 * here is the same number the table and the overview show.
 *
 * The Linked POS customer card is the dashboard's half of C1: it shows the
 * linked customer's visits and recent purchases, or a same-phone suggestion
 * with a Link button. Nothing links automatically (D57).
 *
 * Spec: DESIGN-BRIEF.md §C and `vMemberDrawer()` in prototype.html.
 */
import React, {useMemo, useState} from 'react';
import {
  attendanceRate,
  batchOf,
  DAY_NAMES,
  fmtDate,
  fmtDateTime,
  marksFor,
  memberBalance,
  memberRequests,
  memberStatus,
  money,
  programOf,
  requestBalance,
  requestStatus,
  requestTotal,
  suggestedPosCustomer,
  todayISO,
  type AttendanceMark,
  type Member,
  type NetlyPayData,
} from '../../../internals/managers/NetlyPayManager';
import {Drawer, GhostButton, PrimaryButton} from '../components/Overlays';
import {MemberStatusChip, RequestStatusChip} from '../components/StatusChip';

const TABS = [
  {key: 'overview', label: 'Overview'},
  {key: 'memberships', label: 'Memberships'},
  {key: 'payments', label: 'Payments'},
  {key: 'attendance', label: 'Attendance'},
  {key: 'timeline', label: 'Timeline'},
  {key: 'details', label: 'Details'},
];

/** The attendance cell colours, keyed by mark. */
const MARK_CLASSES: Record<string, string> = {
  P: 'bg-pos-soft text-pos',
  L: 'bg-warn-soft text-warn',
  A: 'bg-neg-soft text-neg',
  E: 'bg-info-soft text-info',
};

const MARK_LABELS: Record<AttendanceMark, string> = {
  P: 'Present',
  L: 'Late',
  A: 'Absent',
  E: 'Excused',
};

const Field = ({label, value, mono}: {label: string; value: React.ReactNode; mono?: boolean}) => (
  <div className="flex items-baseline justify-between gap-4 py-2 border-b border-borderc last:border-0">
    <span className="text-[12.5px] text-text3">{label}</span>
    <span className={`text-[13px] text-text1 text-right ${mono ? 'font-mono tabular-nums' : ''}`}>
      {value}
    </span>
  </div>
);

const Stat = ({label, value, tone = 'default'}: {label: string; value: string; tone?: string}) => (
  <div className="rounded-xl border border-borderc p-3.5">
    <p className="text-[11px] font-semibold tracking-[.06em] text-text3">{label}</p>
    <p
      className={`font-mono font-semibold tabular-nums text-[17px] mt-1.5 ${
        tone === 'danger' ? 'text-neg' : 'text-text1'
      }`}
    >
      {value}
    </p>
  </div>
);

export interface PosCustomer {
  id: string;
  name: string;
  phone: string;
  visits?: number;
  spentMinor?: number;
  last?: string;
  memberId?: string | null;
  purchases?: {date: string; item: string; amountMinor: number}[];
}

interface Props {
  data: NetlyPayData;
  member: Member;
  posCustomers?: PosCustomer[];
  currency?: string;
  onClose: () => void;
  onEdit: () => void;
  onRemind: () => void;
  onLinkCustomer?: (customerId: string) => void;
}

const MemberDrawer = ({
  data,
  member,
  posCustomers = [],
  currency = 'Rs.',
  onClose,
  onEdit,
  onRemind,
  onLinkCustomer,
}: Props) => {
  const [tab, setTab] = useState('overview');
  const today = todayISO();

  const status = memberStatus(data, member, today);
  const batch = batchOf(data, member.batchId);
  const program = programOf(data, member.batchId);
  const balance = memberBalance(data, member);

  const requests = useMemo(
    () => memberRequests(data, member.id).slice().sort((a, b) => b.issued.localeCompare(a.issued)),
    [data, member.id],
  );

  const marks = useMemo(() => marksFor(data, member, today), [data, member, today]);
  const held = marks.filter((m) => m.held && m.mark);
  const rate = attendanceRate(held.map((m) => m.mark));

  const paidThisYear = requests.reduce(
    (t, r) =>
      t +
      (r.payments ?? [])
        .filter((p) => p.date.startsWith(today.slice(0, 4)))
        .reduce((u, p) => u + p.amountMinor, 0),
    0,
  );

  const linked = member.posCustomerId
    ? posCustomers.find((c) => c.id === member.posCustomerId)
    : null;
  const suggestion = linked ? null : suggestedPosCustomer(posCustomers, member);

  /**
   * The timeline is built from what already exists rather than from a feed:
   * requests, their payments and reminders, and the proofs waiting. That way
   * it cannot disagree with the Payments tab.
   */
  const timeline = useMemo(() => {
    const entries: {ts: string; text: string; tone: string}[] = [
      {
        ts: member.joined,
        text: `Joined ${[program?.name, batch?.name].filter(Boolean).join(' · ')}`.trim(),
        tone: 'bg-info',
      },
    ];
    requests.forEach((r) => {
      entries.push({
        ts: r.issued,
        text: `Request ${r.id} sent · ${r.title} · ${money(requestTotal(r), currency)}`,
        tone: 'bg-borderc2',
      });
      (r.payments ?? []).forEach((p) =>
        entries.push({
          ts: p.date,
          text: `Paid ${money(p.amountMinor, currency)} by ${p.method.toLowerCase()} · ${r.id}`,
          tone: 'bg-pos',
        }),
      );
      (r.reminders ?? []).forEach((d) =>
        entries.push({ts: d, text: `Reminder sent for ${r.id}`, tone: 'bg-warn'}),
      );
    });
    (data.approvals ?? [])
      .filter((a) => a.memberId === member.id)
      .forEach((a) =>
        entries.push({
          ts: a.submitted,
          text: `Sent a payment proof for ${a.requestId} · ${money(a.amountMinor, currency)}`,
          tone: 'bg-brand',
        }),
      );
    (member.merged ?? []).forEach((ts) =>
      entries.push({ts, text: 'A duplicate entry was merged into this member', tone: 'bg-brand'}),
    );
    return entries.sort((a, b) => b.ts.localeCompare(a.ts));
  }, [requests, data.approvals, member, program, batch, currency]);

  const note =
    status === 'frozen'
      ? member.freezeNote || 'Membership frozen. No requests are sent while frozen.'
      : status === 'expired'
      ? 'Membership expired. Renew to send new requests.'
      : status === 'pending'
      ? `Joined from the invite link on ${fmtDate(member.joined)}. Approve to start billing.`
      : null;

  return (
    <Drawer
      title={member.name}
      sub={`${member.id} · ${[program?.name, batch?.name].filter(Boolean).join(' · ')}`}
      width={640}
      onClose={onClose}
      tabs={TABS}
      activeTab={tab}
      onTab={setTab}
      footer={
        <>
          <GhostButton onClick={onEdit}>Edit member</GhostButton>
          <PrimaryButton onClick={onRemind} disabled={balance <= 0}>
            Send a reminder
          </PrimaryButton>
          <span className="ml-auto text-[12.5px] text-text3">
            Balance{' '}
            <strong className={`font-mono ${balance > 0 ? 'text-text1' : 'text-text3'}`}>
              {money(balance, currency)}
            </strong>
          </span>
        </>
      }
    >
      {note && (
        <div className="mb-4 rounded-xl border border-borderc bg-surface2 px-4 py-3">
          <p className="text-[12.5px] text-text2">{note}</p>
        </div>
      )}

      {tab === 'overview' && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <MemberStatusChip status={status} />
            <span className="text-[12.5px] text-text3 font-mono">{member.phone}</span>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <Stat label="BALANCE" value={money(balance, currency)} tone={balance > 0 ? 'danger' : 'default'} />
            <Stat label="PAID THIS YEAR" value={money(paidThisYear, currency)} />
            <Stat label="ATTENDANCE" value={rate == null ? '—' : `${rate}%`} />
          </div>

          <div className="rounded-xl border border-borderc p-4">
            <p className="text-[13px] font-semibold text-text1 mb-1">Linked POS customer</p>
            {linked ? (
              <>
                <p className="text-[12.5px] text-text2">
                  {linked.name} · {linked.visits ?? 0} visits ·{' '}
                  {money(linked.spentMinor ?? 0, currency)} spent
                  {linked.last ? ` · last ${fmtDate(linked.last)}` : ''}
                </p>
                {!!linked.purchases?.length && (
                  <div className="mt-3 flex flex-col divide-y divide-borderc">
                    {linked.purchases.slice(0, 3).map((p, i) => (
                      <div key={i} className="py-2 flex items-center justify-between gap-3">
                        <span className="text-[12.5px] text-text1 truncate">{p.item}</span>
                        <span className="text-[12px] font-mono tabular-nums text-text2 flex-shrink-0">
                          {fmtDate(p.date)} · {money(p.amountMinor, currency)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : suggestion ? (
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="text-[12.5px] text-text2">
                  <strong className="text-text1">{suggestion.name}</strong> buys at the counter on
                  the same phone number.
                </p>
                <GhostButton onClick={() => onLinkCustomer?.(suggestion.id)}>Link</GhostButton>
              </div>
            ) : (
              <p className="text-[12.5px] text-text3">
                Not linked. Linking shows this member's counter purchases here.
              </p>
            )}
          </div>
        </div>
      )}

      {tab === 'memberships' && (
        <div className="flex flex-col gap-3">
          <div className="rounded-xl border border-borderc p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[13.5px] font-semibold text-text1">
                  {[program?.name, batch?.name].filter(Boolean).join(' · ') || 'No batch'}
                </p>
                <p className="text-[12.5px] text-text2 mt-0.5">
                  {batch?.coach ? `${batch.coach} · ` : ''}
                  {batch?.venue ?? ''}
                </p>
                <p className="text-[12px] font-mono text-text3 mt-1">
                  {(batch?.slots ?? [])
                    .map((s) => `${DAY_NAMES[s.day]} ${s.start}`)
                    .join(', ') || '—'}
                </p>
              </div>
              <div className="text-right">
                <MemberStatusChip status={status} />
                <p className="text-[13px] font-mono tabular-nums text-text1 mt-2">
                  {money(batch?.feeMinor ?? 0, currency)}
                  <span className="text-[11px] text-text3"> /month</span>
                </p>
              </div>
            </div>
          </div>
          <p className="text-[11.5px] text-text3">
            A member belongs to one batch. Moving them is an edit, which keeps every past request as
            it was.
          </p>
        </div>
      )}

      {tab === 'payments' && (
        <div className="flex flex-col gap-2.5">
          {requests.length === 0 && (
            <p className="text-[13px] text-text3 py-6 text-center">No requests yet.</p>
          )}
          {requests.map((r) => {
            const rs = requestStatus(r, today);
            return (
              <div key={r.id} className="rounded-xl border border-borderc p-3.5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[13px] font-semibold text-text1 truncate">{r.title}</p>
                    <p className="text-[11.5px] font-mono text-text3 mt-0.5">
                      {r.id} · due {fmtDate(r.due)}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <RequestStatusChip status={rs} />
                    <p className="text-[13px] font-mono tabular-nums text-text1 mt-1.5">
                      {money(requestTotal(r), currency)}
                    </p>
                    {requestBalance(r) > 0 && (
                      <p className="text-[11.5px] font-mono text-text3">
                        {money(requestBalance(r), currency)} left
                      </p>
                    )}
                  </div>
                </div>
                {!!r.payments?.length && (
                  <div className="mt-2.5 pt-2.5 border-t border-borderc flex flex-col gap-1">
                    {r.payments.map((p, i) => (
                      <p key={i} className="text-[12px] text-text2">
                        <span className="font-mono tabular-nums">{fmtDate(p.date)}</span> ·{' '}
                        {money(p.amountMinor, currency)} · {p.method}
                        {p.source === 'POS' ? ' · at the counter' : ''}
                        {p.ref ? ` · ${p.ref}` : ''}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {tab === 'attendance' && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-[13px] text-text2">
              {held.length} sessions held ·{' '}
              <strong className={rate != null && rate < 75 ? 'text-neg' : 'text-text1'}>
                {rate == null ? 'not marked yet' : `${rate}%`}
              </strong>
            </p>
            <span className="text-[11.5px] text-text3">(present + late) ÷ held</span>
          </div>

          <div className="flex flex-wrap gap-1.5">
            {marks.map(({session, mark, held: wasHeld}) => (
              <span
                key={session.id}
                title={`${fmtDate(session.date)}${mark ? ` · ${MARK_LABELS[mark]}` : ''}`}
                className={`w-8 h-8 rounded-lg text-[11px] font-semibold flex items-center justify-center ${
                  !wasHeld
                    ? 'bg-surface2 text-text3 opacity-60'
                    : mark
                    ? MARK_CLASSES[mark]
                    : 'bg-surface3 text-text3'
                }`}
              >
                {wasHeld ? mark ?? '·' : session.date.slice(8)}
              </span>
            ))}
            {marks.length === 0 && (
              <p className="text-[13px] text-text3 py-4">No sessions for this batch yet.</p>
            )}
          </div>

          <div className="flex gap-3 flex-wrap">
            {(Object.keys(MARK_LABELS) as AttendanceMark[]).map((m) => (
              <span key={m} className="flex items-center gap-1.5 text-[11.5px] text-text3">
                <span
                  className={`w-4 h-4 rounded text-[10px] font-semibold flex items-center justify-center ${MARK_CLASSES[m]}`}
                >
                  {m}
                </span>
                {MARK_LABELS[m]}
              </span>
            ))}
          </div>
          <p className="text-[11.5px] text-text3">
            Coaches mark attendance in the app. Excused sessions are left out of the rate rather
            than counted either way.
          </p>
        </div>
      )}

      {tab === 'timeline' && (
        <div className="flex flex-col">
          {timeline.map((e, i) => (
            <div key={`${e.ts}-${i}`} className="flex gap-3 pb-4 last:pb-0">
              <span className="flex flex-col items-center flex-shrink-0 pt-1.5">
                <span className={`w-2 h-2 rounded-full ${e.tone}`} />
                {i < timeline.length - 1 && <span className="flex-1 w-px bg-borderc mt-1" />}
              </span>
              <div className="pb-1">
                <p className="text-[13px] text-text1">{e.text}</p>
                <p className="text-[11.5px] font-mono text-text3 mt-0.5">{fmtDateTime(e.ts)}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'details' && (
        <div className="flex flex-col gap-5">
          <div>
            <p className="text-[13px] font-semibold text-text1 mb-1">Member</p>
            <Field label="Member no." value={member.id} mono />
            <Field label="Phone" value={member.phone} mono />
            <Field label="Email" value={member.email || '—'} />
            <Field label="Date of birth" value={member.dob ? fmtDate(member.dob) : '—'} mono />
            <Field label="Joined" value={fmtDate(member.joined)} mono />
          </div>

          <div>
            <p className="text-[13px] font-semibold text-text1 mb-1">Guardians</p>
            {member.guardians?.length ? (
              member.guardians.map((g, i) => (
                <Field
                  key={i}
                  label={`${g.name}${g.relation ? ` · ${g.relation}` : ''}`}
                  value={g.phone}
                  mono
                />
              ))
            ) : (
              <p className="text-[12.5px] text-text3 py-2">Adult member — no guardian recorded.</p>
            )}
          </div>

          <div>
            <p className="text-[13px] font-semibold text-text1 mb-1">Other details</p>
            <Field label="Jersey size" value={member.custom?.jersey || '—'} />
            <Field label="School" value={member.custom?.school || '—'} />
            <Field label="Medical notes" value={member.custom?.medical || '—'} />
          </div>
        </div>
      )}
    </Drawer>
  );
};

export default MemberDrawer;
